import React, {useState} from "react";
import {
  Card,
  Button,
  Image,
  Modal,
  Badge,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import {
  FaPlus,
  FaTimes,
  FaPoll,
  FaLink,
  FaGift,
  FaClock,
  FaArrowUp,
  FaArrowDown,
  FaEdit,
  FaEye,
  FaWhatsapp,
  FaCalendarAlt,
  FaFilePdf,
} from "react-icons/fa";

const CommunicationSequencer = ({
  comunicados,
  setComunicados,
  currentIndex,
  setCurrentIndex,
  incentives = [],
  onShowPollModal,
  onShowLinkModal,
}) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  // Función para agregar un nuevo comunicado
  const addComunicado = () => {
    const newComunicado = {
      id: Date.now(),
      title: `Comunicado ${comunicados.length + 1}`,
      message: "",
      imagePreview: null,
      videoPreview: null,
      poll: null,
      link: null,
      incentiveId: null,
      delayDays: 0,
      delayHours: 0,
    };
    setComunicados((prev) => [...prev, newComunicado]);
    setCurrentIndex(comunicados.length);
  };

  // Función para eliminar un comunicado
  const removeComunicado = (index) => {
    if (comunicados.length > 1) {
      setComunicados((prev) => prev.filter((_, i) => i !== index));
      if (currentIndex >= index && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    }
  };

  // Función para mover comunicado hacia arriba
  const moveComunicadoUp = (index) => {
    if (index > 0) {
      const newComunicados = [...comunicados];
      [newComunicados[index - 1], newComunicados[index]] = [
        newComunicados[index],
        newComunicados[index - 1],
      ];
      setComunicados(newComunicados);
      if (currentIndex === index) setCurrentIndex(index - 1);
      else if (currentIndex === index - 1) setCurrentIndex(index);
    }
  };

  // Función para mover comunicado hacia abajo
  const moveComunicadoDown = (index) => {
    if (index < comunicados.length - 1) {
      const newComunicados = [...comunicados];
      [newComunicados[index], newComunicados[index + 1]] = [
        newComunicados[index + 1],
        newComunicados[index],
      ];
      setComunicados(newComunicados);
      if (currentIndex === index) setCurrentIndex(index + 1);
      else if (currentIndex === index + 1) setCurrentIndex(index);
    }
  };

  // Función para calcular la fecha de envío de cada comunicado
  const calculateSendDate = (index, baseDate = new Date()) => {
    let totalDays = 0;
    let totalHours = 0;

    for (let i = 0; i <= index; i++) {
      totalDays += comunicados[i]?.delayDays || 0;
      totalHours += comunicados[i]?.delayHours || 0;
    }

    const sendDate = new Date(baseDate);
    sendDate.setDate(sendDate.getDate() + totalDays);
    sendDate.setHours(sendDate.getHours() + totalHours);

    return sendDate;
  };

  // Función para obtener el nombre del incentivo
  const getIncentiveName = (incentiveId) => {
    const incentive = incentives.find(
      (i) => i.id.toString() === incentiveId?.toString()
    );
    return incentive?.name || "Ninguno";
  };

  // Componente para mostrar la vista previa de un comunicado
  const ComunicadoPreview = ({comunicado, index}) => (
    <div className="whatsapp-preview mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          <FaCalendarAlt className="me-1" />
          {index === 0
            ? "Inmediato"
            : `${calculateSendDate(index).toLocaleString()}`}
        </small>
        <Badge bg="primary" pill>
          {index + 1}
        </Badge>
      </div>

      <div
        className="rounded-3 shadow-sm ms-auto p-2"
        style={{
          maxWidth: "80%",
          backgroundColor: "#DCF8C6",
          border: "1px solid #e0e0e0",
        }}
      >
        {comunicado.title && (
          <div className="fw-bold mb-1 text-primary">{comunicado.title}</div>
        )}
        {/* Contenido adjunto ahora dentro de la burbuja */}
        {comunicado.imagePreview && (
          <Image
            src={comunicado.imagePreview}
            fluid
            rounded
            className="mb-1"
            style={{maxHeight: "100px"}}
          />
        )}
        {comunicado.videoPreview && (
          <video
            src={comunicado.videoPreview}
            muted
            controls
            className="mb-1"
            style={{
              maxHeight: "100px",
              maxWidth: "100%",
              borderRadius: "6px",
            }}
          />
        )}
        {comunicado.poll && (
          <div className="mb-1 p-2 bg-light rounded border">
            <div className="fw-bold small">
              <FaPoll className="me-1" />
              {comunicado.poll.question}
            </div>
            <div className="small text-muted">
              {comunicado.poll.options.length} opciones
            </div>
          </div>
        )}
        {comunicado.link && (
          <div className="mb-1 p-2 bg-light rounded border">
            <small className="text-primary d-block">
              <FaLink className="me-1" />
              Enlace
            </small>
            <small className="text-muted" style={{wordBreak: "break-all"}}>
              {comunicado.link}
            </small>
          </div>
        )}
        {comunicado.documentPreview && (
          <div className="mb-1 p-2 bg-light rounded border">
            <small className="text-danger d-block">
              <FaFilePdf className="me-1" />
              Documento PDF
            </small>
            <small className="text-muted" style={{wordBreak: "break-all"}}>
              {comunicado.documentPreview.name}
            </small>
          </div>
        )}
        {comunicado.message && (
          <div style={{whiteSpace: "pre-wrap", fontSize: "0.9rem"}}>
            {comunicado.message.length > 100
              ? comunicado.message.substring(0, 100) + "..."
              : comunicado.message}
          </div>
        )}

        {comunicado.incentiveId && (
          <div className="mt-2">
            <Badge bg="success" className="small">
              <FaGift className="me-1" />
              {getIncentiveName(comunicado.incentiveId)}
            </Badge>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Timeline de Comunicados */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-bold">
            <FaClock className="me-2 text-primary" />
            Secuencia de Comunicados ({comunicados.length})
          </h6>
          <Button variant="outline-primary" size="sm" onClick={addComunicado}>
            <FaPlus className="me-1" />
            Agregar Comunicado
          </Button>
        </Card.Header>
        <Card.Body className="p-2">
          {comunicados.length === 0 ? (
            <Alert variant="info" className="text-center m-3">
              <FaWhatsapp className="mb-2" size={24} />
              <div>No hay comunicados en la secuencia</div>
              <Button
                variant="primary"
                size="sm"
                onClick={addComunicado}
                className="mt-2"
              >
                Crear Primer Comunicado
              </Button>
            </Alert>
          ) : (
            <Row>
              {/* Lista de Comunicados */}
              <Col md={6}>
                <div style={{maxHeight: "400px", overflowY: "auto"}}>
                  {comunicados.map((comunicado, index) => (
                    <Card
                      key={comunicado.id}
                      className={`mb-2 ${
                        currentIndex === index ? "border-primary" : ""
                      }`}
                      style={{cursor: "pointer"}}
                    >
                      <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div
                            className="flex-grow-1"
                            onClick={() => setCurrentIndex(index)}
                          >
                            <div className="d-flex align-items-center mb-1">
                              <Badge
                                bg={
                                  currentIndex === index
                                    ? "primary"
                                    : "secondary"
                                }
                                pill
                                className="me-2"
                              >
                                {index + 1}
                              </Badge>
                              <strong className="small">
                                {comunicado.title || `Comunicado ${index + 1}`}
                              </strong>
                            </div>

                            <div className="small text-muted mb-1">
                              {index === 0 ? (
                                <span>
                                  <FaClock className="me-1" />
                                  Inmediato
                                </span>
                              ) : (
                                <span>
                                  <FaClock className="me-1" />+
                                  {comunicado.delayDays}d{" "}
                                  {comunicado.delayHours}h
                                </span>
                              )}
                            </div>

                            <div className="small text-truncate">
                              {comunicado.message || (
                                <em className="text-muted">Sin mensaje</em>
                              )}
                            </div>

                            <div className="d-flex gap-1 mt-1">
                              {comunicado.imagePreview && (
                                <Badge bg="info" className="small">
                                  IMG
                                </Badge>
                              )}
                              {comunicado.videoPreview && (
                                <Badge bg="warning" className="small">
                                  VID
                                </Badge>
                              )}
                              {comunicado.poll && (
                                <Badge bg="success" className="small">
                                  POLL
                                </Badge>
                              )}
                              {comunicado.link && (
                                <Badge bg="primary" className="small">
                                  LINK
                                </Badge>
                              )}
                              {comunicado.documentPreview && (
                                <Badge bg="danger" className="small">
                                  PDF
                                </Badge>
                              )}
                              {comunicado.incentiveId && (
                                <Badge bg="danger" className="small">
                                  GIFT
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="d-flex flex-column gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                setPreviewIndex(index);
                                setShowPreviewModal(true);
                              }}
                              title="Vista previa"
                            >
                              <FaEye />
                            </Button>

                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => moveComunicadoUp(index)}
                                disabled={index === 0}
                                title="Mover arriba"
                              >
                                <FaArrowUp />
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => moveComunicadoDown(index)}
                                disabled={index === comunicados.length - 1}
                                title="Mover abajo"
                              >
                                <FaArrowDown />
                              </Button>
                            </div>

                            {comunicados.length > 1 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeComunicado(index)}
                                title="Eliminar"
                              >
                                <FaTimes />
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Col>

              {/* Vista Previa en Tiempo Real */}
              <Col md={6}>
                <div className="sticky-top">
                  <h6 className="fw-bold mb-3">Vista Previa de la Secuencia</h6>
                  <div
                    style={{
                      backgroundColor: "#E5DDD5",
                      borderRadius: "10px",
                      padding: "1rem",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {comunicados.map((comunicado, index) => (
                      <ComunicadoPreview
                        key={comunicado.id}
                        comunicado={comunicado}
                        index={index}
                      />
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Vista Previa Detallada */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Vista Previa -{" "}
            {comunicados[previewIndex]?.title ||
              `Comunicado ${previewIndex + 1}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {comunicados[previewIndex] && (
            <div
              style={{
                backgroundColor: "#E5DDD5",
                borderRadius: "10px",
                padding: "2rem",
                minHeight: "300px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <div
                className="rounded-3 shadow-sm ms-auto p-3"
                style={{
                  maxWidth: "80%",
                  backgroundColor: "#DCF8C6",
                }}
              >
                {comunicados[previewIndex].title && (
                  <div className="fw-bold mb-2 text-primary fs-5">
                    {comunicados[previewIndex].title}
                  </div>
                )}

                {comunicados[previewIndex].imagePreview && (
                  <div className="mb-3">
                    <Image
                      src={comunicados[previewIndex].imagePreview}
                      fluid
                      rounded
                    />
                  </div>
                )}

                {comunicados[previewIndex].videoPreview && (
                  <div className="mb-3">
                    <video
                      src={comunicados[previewIndex].videoPreview}
                      controls
                      muted
                      style={{borderRadius: "6px", maxWidth: "100%"}}
                    />
                  </div>
                )}

                {comunicados[previewIndex].poll && (
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="fw-bold mb-2">
                      {comunicados[previewIndex].poll.question}
                    </div>
                    {comunicados[previewIndex].poll.options.map((option, i) => (
                      <div key={i} className="p-2 mb-1 bg-white rounded border">
                        {option}
                      </div>
                    ))}
                  </div>
                )}

                {comunicados[previewIndex].link && (
                  <div className="mb-3">
                    <a
                      href={comunicados[previewIndex].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none text-primary fw-bold"
                    >
                      <FaLink className="me-2" />
                      {comunicados[previewIndex].link}
                    </a>
                  </div>
                )}

                {comunicados[previewIndex].documentPreview && (
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="fw-bold mb-2">
                      <FaFilePdf className="me-2 text-danger" />
                      Documento Adjunto
                    </div>
                    <a
                      href={comunicados[previewIndex].documentPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {comunicados[previewIndex].documentPreview.name}
                    </a>
                  </div>
                )}

                {comunicados[previewIndex].message && (
                  <div
                    style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}
                  >
                    {comunicados[previewIndex].message}
                  </div>
                )}

                {comunicados[previewIndex].incentiveId && (
                  <div className="mt-3">
                    <Badge bg="success" className="fs-6">
                      <FaGift className="me-2" />
                      {getIncentiveName(comunicados[previewIndex].incentiveId)}
                    </Badge>
                  </div>
                )}
              </div>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Comunicado {previewIndex + 1} de {comunicados.length} •
                  {previewIndex === 0
                    ? " Envío inmediato"
                    : ` Envío: ${calculateSendDate(
                        previewIndex
                      ).toLocaleString()}`}
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPreviewModal(false)}
          >
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setCurrentIndex(previewIndex);
              setShowPreviewModal(false);
            }}
          >
            <FaEdit className="me-2" />
            Editar Este Comunicado
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CommunicationSequencer;
